<template>
  <div class="calendar-page">
    <div v-if="loading" class="loading">Chargement...</div>

    <div class="page-header">
      <div>
        <h1 class="page-heading">Calendrier des événements</h1>
        <p class="page-subheading">
          {{ activeTab === 'officiel'
            ? 'Tournois officiels de la Fédération, des ligues et des comités, partout en France.'
            : 'Tournois locaux organisés par les clubs, en dehors du calendrier officiel.' }}
        </p>
      </div>
      <div class="filters">
        <div class="season-picker">
          <label for="season-select">Saison</label>
          <select id="season-select" v-model="selectedSeason">
            <option v-for="season in availableSeasons" :value="season" :key="season">
              {{ season }}
            </option>
          </select>
        </div>
        <div class="season-picker">
          <label for="ligue-select">Ligue</label>
          <select id="ligue-select" v-model="selectedLigue">
            <option value="">Toutes</option>
            <option v-for="ligue in ligueOptions" :value="ligue.code" :key="ligue.code">
              {{ ligue.name }}
            </option>
          </select>
        </div>
        <div class="season-picker">
          <label for="comite-select">Comité</label>
          <select id="comite-select" v-model="selectedComite">
            <option value="">Tous</option>
            <option v-for="comite in comiteOptions" :value="comite.code" :key="comite.code">
              {{ comite.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="tab-switch">
      <button type="button" :class="{ active: activeTab === 'officiel' }" @click="switchTab('officiel')">
        Tournois officiels
      </button>
      <button type="button" :class="{ active: activeTab === 'local' }" @click="switchTab('local')">
        Tournois locaux
      </button>
    </div>

    <div v-if="Object.keys(groupedEvents).length" class="timeline">
      <section v-for="(events, month) in groupedEvents" class="month-group" :key="month">
        <h2 class="month-header">{{ month }}</h2>
        <div class="event-list">
          <article v-for="event in events" class="event-row" @click="openEvent(event)" :key="event.id">
            <div class="date-badge">
              <span class="date-day" :class="{ 'date-day-range': dateBadge(event).isRange }">{{ dateBadge(event).day }}</span>
              <span class="date-month">{{ dateBadge(event).month }}</span>
            </div>

            <div class="event-body">
              <h3 class="event-name">{{ displayEventName(event) }}</h3>
              <p class="event-meta">
                <span v-if="event.organiser"><i class="fa-solid fa-shield-halved"></i> {{ event.organiser }}</span>
                <span v-if="event.place"><i class="fa-solid fa-location-dot"></i> {{ titleCase(event.place) }}</span>
              </p>
              <p v-if="event.description" class="event-description">{{ event.description }}</p>
            </div>

            <div class="event-side">
              <button v-if="homologableIds.has(event.id)" type="button" class="btn-outline btn-homologation"
                @click.stop="openHomologation(event)">
                <i class="fa-solid fa-clipboard-check"></i> Homologation
              </button>
              <span v-if="formatPriceParts(event).length" class="price-chip">
                <span v-for="part in formatPriceParts(event)" class="price-part" :key="part">{{ part }}</span>
              </span>
              <img v-if="getEventImage(event) && !failedImages.has(event.id)" :src="getEventImage(event)!"
                alt="Affiche du tournoi" class="event-thumb" @error="onImageError(event.id)" />
              <div v-else class="event-thumb poster-placeholder">
                <i class="fa-solid fa-image"></i>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div v-else class="no-events">
      <p v-if="selectedLigue || selectedComite">Aucun tournoi ne correspond à ces filtres pour cette saison.</p>
      <p v-else>Aucun tournoi disponible pour cette saison.</p>
    </div>

    <!-- Le détail d'un événement (affiche, infos, "S'inscrire"...) a sa propre page
    partageable, /evenement/:id (cf. EventDetails.vue) -- plus de modale ici. -->

    <!-- Homologation d'un tournoi de comité : participants licenciés / non licenciés par
    catégorie, d'après le classement importé (cf. leaderboards/event_routes.py::
    get_event_homologation). Quantités seulement. -->
    <div v-if="homologationEvent" class="event-modal" @click.self="homologationEvent = null">
      <div class="modal-card">
        <button class="modal-close" aria-label="Fermer" @click="homologationEvent = null">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal-title">Homologation</h3>
        <p class="modal-subtitle">{{ displayEventName(homologationEvent) }}</p>
        <p v-if="!homologation" class="loading-inline">Chargement...</p>
        <template v-else>
          <table class="homologation-table">
            <thead>
              <tr>
                <th>Catégorie</th>
                <th>Licenciés</th>
                <th>Non licenciés</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in homologation.categories" :key="c.category">
                <td>
                  {{ categoryLabel(c.category) }}
                  <span v-if="c.teams !== null" class="teams-note">{{ c.teams }} équipe{{ c.teams > 1 ? 's' : '' }}</span>
                </td>
                <td>{{ c.licensed }}</td>
                <td>{{ c.non_licensed }}</td>
                <td><strong>{{ c.total }}</strong></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total des participations</td>
                <td>{{ homologation.total.licensed }}</td>
                <td>{{ homologation.total.non_licensed }}</td>
                <td><strong>{{ homologation.total.total }}</strong></td>
              </tr>
            </tfoot>
          </table>
          <p class="homologation-note">
            D'après le classement du tournoi (0 point compris). Un double compte ses deux joueurs ;
            un joueur engagé dans plusieurs catégories compte dans chacune.
          </p>
        </template>
      </div>
    </div>

    <!-- Modale création/édition d'un tournoi local -- réservée aux représentants de club
    (pour leur propre club) et aux admins, cf. canCreateLocal. Création seulement : les
    infos se modifient ensuite depuis les paramètres du tournoi (EventInscriptions.vue). Reprend
    les champs du même formulaire que CMER (LocalCalendar.vue), qui poste déjà sur ce
    même backend partagé (POST/PATCH /events/local) -- juste une autre UI dessus. -->
    <div v-if="showLocalForm" class="event-modal" @click.self="closeLocalForm">
      <div class="modal-card">
        <button class="modal-close" aria-label="Fermer" @click="closeLocalForm">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal-title">Ajouter un tournoi local</h3>

        <label class="form-label">Nom du tournoi *</label>
        <input v-model="localForm.name" placeholder="Open de mon club" class="form-input" />

        <label class="form-label">Club organisateur</label>
        <input v-model="localForm.club_name" class="form-input" readonly style="opacity: 0.7; cursor: default;" />

        <label class="form-label">Lieu (ville, département...)</label>
        <input v-model="localForm.location" placeholder="Montpellier (34)" class="form-input" />

        <label class="form-label">Date de début *</label>
        <input v-model="localForm.event_date" type="date" class="form-input" />

        <label class="form-label">Date de fin (optionnel)</label>
        <input v-model="localForm.event_date_end" type="date" class="form-input" />

        <label class="form-label">Format</label>
        <select v-model="localForm.format" class="form-input">
          <option value="simple">Simple</option>
          <option value="double">Double</option>
          <option value="simple+double">Simple + Double</option>
        </select>

        <label class="form-label">Prix par joueur et par catégorie (€)</label>
        <input v-model.number="localForm.price" type="number" min="0" step="0.01" placeholder="0.00" class="form-input" />

        <label class="form-label">Lien externe (infos / inscription)</label>
        <input v-model="localForm.external_link" type="url" placeholder="https://..." class="form-input" />

        <label class="form-label">Description</label>
        <textarea v-model="localForm.description" placeholder="Informations complémentaires..."
          class="form-input form-textarea" rows="4" />

        <label class="form-label">Affiche</label>
        <input type="file" class="form-input" accept="image/*" @change="onLocalAfficheChange" />

        <div class="modal-actions">
          <button class="btn-primary" @click="submitLocalForm">
            Ajouter
          </button>
          <button class="btn-outline" @click="closeLocalForm">Annuler</button>
        </div>
      </div>
    </div>

    <button v-if="canCreateLocal" class="add-local-button" @click="openLocalCreateForm">
      <i class="fa-solid fa-plus"></i> Ajouter un tournoi
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import backendApi from '@/axios/backendApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEventStore } from '@/stores/useEventStore';
import { getCurrentSeason, getSeasonFromDate } from '@/config/config';
import { titleCase } from '@/functions/utils';
import { RANKING_CATEGORIES } from '@/pages/admin/import-classement/types';
import {
  displayEventName, formatPriceParts, getEventImage, loadJurisdictionNames,
  type JurisdictionNames, eventPath,
} from '@/functions/eventDisplay';

const route = useRoute();
const router = useRouter();
const eventStore = useEventStore();
const authStore = useAuthStore();
const loading = ref(false);

// Bascule officiel/local -- mêmes deux catégories que sur CMER (LocalCalendar.vue), qui
// crée déjà ses "tournois locaux" via ce même backend partagé (POST /events/local) : une
// seule liste affichée à la fois, rechargée à chaque bascule plutôt que de garder deux
// listes en mémoire pour rien.
const activeTab = ref<'officiel' | 'local'>('officiel');
const switchTab = async (tab: 'officiel' | 'local') => {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  await eventStore.fetchEvents(tab);
};

// Chaque ligne du calendrier mène à la page dédiée de l'événement (affiche, infos,
// "S'inscrire"...), partageable par son URL -- cf. EventDetails.vue.
const openEvent = (event: OfficialEvent) => {
  router.push(eventPath(event));
};

// Code -> nom complet, chargé une fois (cf. eventDisplay.ts::loadJurisdictionNames).
const comiteNames = ref<JurisdictionNames>({});
const ligueNames = ref<JurisdictionNames>({});
const comiteLigues = ref<JurisdictionNames>({});

const selectedSeason = ref(getCurrentSeason());

// Filtres ligue/comité : event.ligue/event.comite sont ceux du club organisateur
// (renseignés pour l'officiel comme pour le local), donc "Ligue X" garde aussi les
// tournois de ses comités et de ses clubs, pas seulement ceux organisés par la ligue.
// Mais un joueur marque des points à trois niveaux (son comité, sa ligue, la FFD) : un
// filtre garde donc aussi les tournois des niveaux au-dessus, d'après event.level
// (cf. Dartshub clubs/premium.py::official_level) -- "Comité MER" montre les tournois de
// ligue de Sud-Est où qu'ils soient organisés, et tout filtre montre les fédéraux.
const selectedLigue = ref('');
const selectedComite = ref('');

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'fr');
const ligueOptions = computed(() =>
  Object.entries(ligueNames.value).map(([code, name]) => ({ code, name })).sort(byName));
const comiteOptions = computed(() =>
  Object.entries(comiteNames.value)
    .filter(([code]) => !selectedLigue.value || comiteLigues.value[code] === selectedLigue.value)
    .map(([code, name]) => ({ code, name }))
    .sort(byName));

// Un comité hors de la nouvelle ligue choisie ne peut plus rien afficher.
watch(selectedLigue, () => {
  if (selectedComite.value && !comiteOptions.value.some((c) => c.code === selectedComite.value)) {
    selectedComite.value = '';
  }
});
// Choisir un comité fixe sa ligue (le comité appartient à une seule ligue).
watch(selectedComite, (code) => {
  const ligue = code && comiteLigues.value[code];
  if (ligue) selectedLigue.value = ligue;
});

// Un licencié voit d'abord les tournois où il marque des points : filtre sur son comité
// (qui fixe sa ligue, cf. watch ci-dessus), à défaut sur sa ligue. Présélection appliquée
// une seule fois : la licence (Auth.vue::fetchLicense) et les noms des comités/ligues
// arrivent en différé, et un retour sur "Tous"/"Toutes" ne doit pas être écrasé ensuite.
// Une licence FFD (rattachement direct à la fédération) n'est pas une ligue : "FFD" ne
// garderait que les tournois fédéraux.
let jurisdictionDefaulted = false;
watch([() => authStore.license, comiteNames, ligueNames], ([license]) => {
  if (jurisdictionDefaulted || selectedLigue.value || selectedComite.value || !license) return;
  if (license.comite && comiteLigues.value[license.comite]) {
    jurisdictionDefaulted = true;
    selectedComite.value = license.comite;
  } else if (license.ligue && license.ligue !== 'FFD' && ligueNames.value[license.ligue]) {
    jurisdictionDefaulted = true;
    selectedLigue.value = license.ligue;
  }
}, { immediate: true });

const matchesJurisdiction = (event: OfficialEvent) => {
  if (!selectedLigue.value && !selectedComite.value) return true;
  if (event.level === 'federal') return true;
  if (event.level === 'ligue') return event.ligue === selectedLigue.value;
  if (selectedComite.value) return event.comite === selectedComite.value;
  return event.ligue === selectedLigue.value;
};

// La création et l'édition des événements officiels (nom, date, club organisateur,
// affiche...) se font exclusivement dans LSEF, l'outil de gestion du calendrier officiel
// réservé à la fédération, à la ligue et aux comités -- Calendrier n'affiche l'officiel
// qu'en lecture seule, comme CMER.
// Un tournoi local appartient TOUJOURS au club du représentant qui le crée (jamais un
// choix libre, cf. events/routes.py::create_local_event qui réattribue de toute façon à
// son club même si un autre id était envoyé), même logique que CMER (LocalCalendar.vue) :
// seul un club_representative (son propre club) peut créer, pas l'admin/comité.
const canCreateLocal = computed(() => activeTab.value === 'local' && authStore.isAuthenticated
  && authStore.scopes.includes('club_representative'));

const userClubName = ref('');
const fetchMyClub = async () => {
  try {
    const { data } = await backendApi.get('/clubs/my-club');
    userClubName.value = data.name || '';
  } catch { /* pas de club rattaché -- pas grave, juste pas d'auto-remplissage */ }
};

const showLocalForm = ref(false);
const localAffiche = ref<File | null>(null);
const localForm = ref({
  name: '', club_name: '', location: '', event_date: '', event_date_end: '',
  format: 'simple+double', price: null as number | null, external_link: '', description: '',
});

const resetLocalForm = () => {
  localForm.value = {
    name: '', club_name: userClubName.value, location: '', event_date: '', event_date_end: '',
    format: 'simple+double', price: null, external_link: '', description: '',
  };
  localAffiche.value = null;
};

const openLocalCreateForm = async () => {
  resetLocalForm();
  showLocalForm.value = true;
};

const closeLocalForm = () => {
  showLocalForm.value = false;
};

const onLocalAfficheChange = (e: Event) => {
  localAffiche.value = (e.target as HTMLInputElement).files?.[0] ?? null;
};

const submitLocalForm = async () => {
  const f = localForm.value;
  if (!f.name.trim() || !f.event_date) {
    toast.error('Nom et date de début requis.');
    return;
  }
  const payload = {
    name: f.name.trim(),
    club_name: f.club_name || null,
    // Pas d'organizer_club_id ici : create_local_event réattribue de toute façon au
    // club du représentant connecté (jamais un choix libre, cf. canCreateLocal ci-dessus).
    location: f.location || null,
    event_date: f.event_date,
    event_date_end: f.event_date_end || null,
    format: f.format || null,
    price_cents: f.price ? Math.round(f.price * 100) : 0,
    external_link: f.external_link || null,
    description: f.description || null,
  };

  try {
    const { data } = await backendApi.post('/events/local', payload);
    if (localAffiche.value) {
      const formData = new FormData();
      formData.append('file', localAffiche.value);
      await backendApi.post(`/events/local/${data.id}/upload-affiche`, formData);
    }
    toast.success('Tournoi ajouté.');
    closeLocalForm();
    await eventStore.fetchEvents('local');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement du tournoi.');
  }
};

// Homologation : bouton sur les tournois de comité dont le classement est importé, pour
// les représentants de ce comité (et le super_admin) -- la liste vient du serveur, qui
// seul sait quels résultats existent et qui représente quel comité.
type HomologationCategory = { category: string; licensed: number; non_licensed: number; total: number; teams: number | null };
type Homologation = { categories: HomologationCategory[]; total: { licensed: number; non_licensed: number; total: number } };
const homologableIds = ref(new Set<number>());
const homologationEvent = ref<OfficialEvent | null>(null);
const homologation = ref<Homologation | null>(null);
const categoryLabel = (c: string) => RANKING_CATEGORIES.find((cat) => cat.value === c)?.label ?? c;

const fetchHomologable = async () => {
  if (!authStore.scopes.some((s) => s === 'comitee_representative' || s === 'super_admin')) return;
  try {
    const { data } = await backendApi.get<number[]>('/leaderboard/event/homologable');
    homologableIds.value = new Set(data);
  } catch { /* pas de bouton, sans gêner l'affichage du calendrier */ }
};

const openHomologation = async (event: OfficialEvent) => {
  homologationEvent.value = event;
  homologation.value = null;
  try {
    const { data } = await backendApi.get<Homologation>(`/leaderboard/event/${event.id}/homologation`);
    if (homologationEvent.value?.id === event.id) homologation.value = data;
  } catch (err: any) {
    homologationEvent.value = null;
    toast.error(err.response?.data?.detail || 'Erreur lors du calcul de l\'homologation.');
  }
};

const failedImages = ref(new Set<number>());
const onImageError = (eventId: number) => {
  failedImages.value.add(eventId);
};

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTH_SHORT = ['JANV', 'FÉVR', 'MARS', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOÛT', 'SEPT', 'OCT', 'NOV', 'DÉC'];

// Tournoi sur plusieurs jours : "17/18" sur deux jours, "17-19" au-delà ; les deux mois
// si le tournoi chevauche une fin de mois ("31/1", "DÉC/JANV").
const dateBadge = (event: OfficialEvent) => {
  const d = new Date(event.date + 'T12:00:00');
  const end = event.date_end && event.date_end !== event.date ? new Date(event.date_end + 'T12:00:00') : null;
  if (!end || isNaN(end.getTime()) || end <= d) {
    return { day: String(d.getDate()), month: MONTH_SHORT[d.getMonth()], isRange: false };
  }
  const nbDays = Math.round((end.getTime() - d.getTime()) / 86400000) + 1;
  const sep = nbDays === 2 ? '/' : '-';
  const sameMonth = d.getMonth() === end.getMonth();
  return {
    day: `${d.getDate()}${sep}${end.getDate()}`,
    month: sameMonth ? MONTH_SHORT[d.getMonth()] : `${MONTH_SHORT[d.getMonth()]}${sep}${MONTH_SHORT[end.getMonth()]}`,
    isRange: true,
  };
};

const availableSeasons = computed(() => {
  const seasons = new Set(eventStore.events.map((event) => getSeasonFromDate(event.date)));
  seasons.add(getCurrentSeason());
  return [...seasons].sort().reverse();
});

const groupedEvents = computed(() => {
  const groups: { [key: string]: OfficialEvent[] } = {};

  const sorted = eventStore.events
    .filter((event) => getSeasonFromDate(event.date) === selectedSeason.value)
    .filter(matchesJurisdiction)
    .sort((a, b) => a.date.localeCompare(b.date));

  sorted.forEach((event) => {
    const d = new Date(event.date + 'T12:00:00');
    const key = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(event);
  });

  return groups;
});

onMounted(async () => {
  // `?tab=local` (retour depuis la page d'un tournoi local) : on ouvre directement le
  // bon onglet. L'édition d'un tournoi local ne se fait plus ici mais dans ses
  // paramètres (EventInscriptions.vue, onglet Paramètres) -- seule la création reste.
  if (route.query.tab === 'local') activeTab.value = 'local';

  const tasks: Promise<unknown>[] = [
    eventStore.fetchEvents(activeTab.value),
    loadJurisdictionNames().then((names) => {
      comiteNames.value = names.comites;
      ligueNames.value = names.ligues;
      comiteLigues.value = names.comiteLigues;
    }),
  ];
  if (authStore.isAuthenticated) tasks.push(fetchMyClub(), fetchHomologable());
  await Promise.all(tasks);
});
</script>

<style scoped>
.calendar-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0.5rem 1rem 3rem;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.75rem;
}

.page-heading {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 0.25rem;
}

.page-subheading {
  color: var(--color-fg-darker);
  margin: 0;
  font-size: 0.9rem;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
}

.season-picker {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.season-picker label {
  font-size: 0.85rem;
  color: var(--color-fg-darker);
  font-weight: 600;
}

.season-picker select {
  width: auto;
  margin: 0;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--color-light-shadow);
  background: var(--color-bg-lighter);
}

.month-group {
  margin-bottom: 2.25rem;
}

.month-header {
  color: var(--color-accent);
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 0.85rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--color-light-shadow);
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.event-row {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  background: var(--color-bg-lighter);
  border: 1px solid var(--color-light-shadow);
  border-left: 3px solid transparent;
  border-radius: var(--radius);
  padding: 0.9rem 1.1rem;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s;
}

.event-row:hover {
  transform: translateY(-2px);
  border-left-color: var(--color-accent);
}

.date-badge {
  flex: 0 0 auto;
  min-width: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
  border-radius: var(--radius);
  padding: 0.5rem 0.35rem;
}

.date-day {
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1;
}

.date-day-range {
  font-size: 1.05rem;
  white-space: nowrap;
}

.date-month {
  font-size: 0.65rem;
  color: var(--color-fg-darker);
  font-weight: 700;
  letter-spacing: 0.05em;
}

.event-body {
  flex: 1;
  min-width: 0;
}

.event-name {
  margin: 0 0 0.3rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.event-meta {
  margin: 0 0 0.3rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  font-size: 0.85rem;
  color: var(--color-fg-darker);
}

.event-meta i {
  color: var(--color-accent);
  margin-right: 0.3rem;
}

.event-description {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-fg-darker);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-side {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.price-chip {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-accent);
  background: rgba(245, 165, 36, 0.12);
  padding: 0.3rem 0.7rem;
  border-radius: var(--radius);
  max-width: 260px;
}

/* Une catégorie par ligne : sur une seule ligne, les tarifs (licencié / non licencié,
   individuel / double) écrasaient le titre, voire débordaient de l'écran sur mobile. */
.price-part {
  display: block;
}

.event-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: var(--radius);
  display: none;
}

.poster-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: var(--color-bg);
  border: 1px dashed var(--color-light-shadow);
  color: var(--color-fg-darker);
  text-align: center;
  font-family: var(--font-main);
}

.event-thumb.poster-placeholder i {
  font-size: 1.1rem;
  color: var(--color-accent);
}

.event-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-card {
  position: relative;
  background: var(--color-bg-lighter);
  border: 1px solid var(--color-light-shadow);
  padding: 1.75rem;
  border-radius: var(--radius);
  max-width: 520px;
  width: 100%;
  max-height: 85vh;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
}

.modal-close {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  background: transparent;
  border: none;
  color: var(--color-fg-darker);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.3rem;
}

.modal-close:hover {
  color: var(--color-fg);
}

.modal-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 800;
}

.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.25rem;
}

.btn-primary,
.btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  border-radius: var(--radius);
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  box-sizing: border-box;
}

.btn-primary {
  background: var(--color-accent);
  color: var(--color-bg);
}

.btn-outline {
  background: transparent;
  border-color: var(--color-light-shadow);
  color: var(--color-fg);
}

.btn-outline:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.btn-danger {
  border-color: var(--color-danger, #d9534f);
  color: var(--color-danger, #d9534f);
}

.btn-danger:hover {
  background: var(--color-danger, #d9534f);
  color: #fff;
}

.btn-homologation {
  white-space: nowrap;
  gap: 0.4rem;
}

.modal-subtitle {
  margin: 0.3rem 0 1rem;
  color: var(--color-fg-darker);
  font-size: 0.9rem;
}

.loading-inline {
  color: var(--color-fg-darker);
}

.homologation-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.homologation-table th,
.homologation-table td {
  padding: 0.45rem 0.5rem;
  border-bottom: 1px solid var(--color-light-shadow);
  text-align: right;
}

.homologation-table th:first-child,
.homologation-table td:first-child {
  text-align: left;
}

.homologation-table tfoot td {
  font-weight: 700;
  border-bottom: none;
}

.teams-note {
  display: block;
  font-size: 0.75rem;
  color: var(--color-fg-darker);
}

.homologation-note {
  margin: 0.9rem 0 0;
  font-size: 0.8rem;
  color: var(--color-fg-darker);
}

.tab-switch {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.tab-switch button {
  padding: 0.5rem 1.1rem;
  border-radius: 999px;
  border: 1px solid var(--color-light-shadow);
  background: transparent;
  color: var(--color-fg-darker);
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
}

.tab-switch button.active {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: var(--color-bg);
}

.add-local-button {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.3rem;
  border-radius: 999px;
  border: none;
  background: var(--color-accent);
  color: var(--color-bg);
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  z-index: 900;
}

.form-label {
  display: block;
  font-size: 0.8rem;
  color: var(--color-fg-darker);
  margin-top: 0.6rem;
  font-weight: 600;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  font-size: inherit;
}

.no-events,
.loading {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-fg-darker);
}

@media (min-width: 560px) {
  .event-thumb {
    display: flex;
  }
}

@media (max-width: 768px) {
  .calendar-page {
    padding-left: 0;
    padding-right: 0;
  }
}

@media (max-width: 560px) {
  .event-row {
    flex-wrap: wrap;
  }

  .event-side {
    width: 100%;
    justify-content: flex-start;
  }

  .price-chip {
    max-width: none;
  }

  /* Sous .event-thumb, la vraie affiche (<img>) reste cachée en dessous de ce seuil
     (cf. règle de base .event-thumb { display: none }), mais le placeholder
     "affiche manquante" a sa propre règle .poster-placeholder { display: flex }, plus
     tardive dans la feuille de style et donc prioritaire à spécificité égale -- il
     restait visible même sans affiche à afficher. */
  .event-thumb.poster-placeholder {
    display: none;
  }

  .modal-title {
    text-align: center;
  }
}
</style>
